<template>
  <div class="page-shell">
    <div class="page-header">
      <div class="page-header__titles">
        <h1 class="page-title">Account Information</h1>
        <p class="page-subtitle">
          Your account profile and registered biometric devices
        </p>
      </div>
    </div>

    <div class="content-card">
      <v-row>
        <v-col cols="6" md="3" v-for="stat in accountStats" :key="stat.label">
          <div class="account-stat">
            <v-icon color="primary" class="mb-2">{{ stat.icon }}</v-icon>
            <div class="account-stat__label">{{ stat.label }}</div>
            <div class="account-stat__value">{{ stat.value }}</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div class="content-card">
      <div class="d-flex align-center mb-1">
        <div>
          <h2 class="section-title">Attendance Data Retention</h2>
          <p class="section-subtitle">
            How long imported attendance is kept. Anything older than this is
            removed automatically the next time you upload an attendance file —
            keeping your database small.
          </p>
        </div>
      </div>
      <v-row align="center" class="mt-1">
        <v-col cols="12" sm="5" md="4">
          <v-select
            v-model="retention_months"
            :items="retention_options"
            label="Keep attendance for"
            outlined
            dense
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="4">
          <v-btn
            color="primary"
            :loading="retention_saving"
            :disabled="retention_months == saved_retention_months"
            @click="save_retention"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <div class="content-card">
      <div class="d-flex align-center mb-4">
        <div>
          <h2 class="section-title">Device Information</h2>
          <p class="section-subtitle">Registered Devices</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn color="primary" small @click="add_device = true">
          <v-icon left small>mdi-plus</v-icon>
          Add Device
        </v-btn>
      </div>
      <v-data-table :items="devices" :headers="device_headers">
        <template v-slot:[`item.actions`]>
          <v-btn icon small color="warning"
            ><v-icon small>mdi-pencil-outline</v-icon></v-btn
          >
          <v-btn icon small color="error" class="ml-1"
            ><v-icon small>mdi-delete-outline</v-icon></v-btn
          >
        </template>
      </v-data-table>
    </div>
    <add-device-modal @close="add_device = false" v-if="add_device" />
    <add-user-modal @close="add_user = false" v-if="add_user" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  created() {
    if (this.devices.length <= 0) this.get_devices();
    this.retention_months = this.saved_retention_months;
  },
  components: {
    "add-device-modal": () => import("@/components/Device/addDeviceModal.vue"),
    "add-user-modal": () => import("@/components/User/addUserModal.vue"),
  },
  computed: {
    ...mapGetters({
      devices: "device/devices",
      user: "auth/user",
    }),
    saved_retention_months() {
      return this.user.attendance_retention_months || 3;
    },
    accountStats() {
      return [
        {
          label: "Username",
          value: this.user.username,
          icon: "mdi-account-circle-outline",
        },
        {
          label: "Device Limit",
          value: this.user.device_limit,
          icon: "mdi-devices",
        },
        {
          label: "Date Created",
          value: this.user.created_at,
          icon: "mdi-calendar-outline",
        },
        {
          label: "Access Level",
          value: this.user.access_level,
          icon: "mdi-shield-account-outline",
        },
      ];
    },
  },
  data() {
    return {
      button_loading: false,
      show_alert: false,
      alert_message: "",
      alert_type: "",
      authentication: "",
      authentication_code: "jmbcomputers2023",
      authentication_dialog: true,
      authenticated: false,
      add_device: false,
      add_user: false,
      retention_months: 3,
      retention_saving: false,
      retention_options: [
        { text: "1 month", value: 1 },
        { text: "2 months", value: 2 },
        { text: "3 months", value: 3 },
      ],
      device_headers: [
        {
          text: "Device ID",
          value: "id",
        },
        { text: "Device Name", value: "device_name" },
        { text: "Username", value: "user.username" },
        {
          text: "Serial Number",
          value: "serial_number",
        },
        {
          text: "Device IP Address",
          value: "ip_address",
        },
        {
          text: "Created at",
          value: "created_at",
        },
        {
          text: "Updated at",
          value: "updated_at",
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      get_devices: "device/get_devices",
      signOut: "auth/signout",
      update_attendance_retention: "auth/update_attendance_retention",
      set_notification: "notification/set_notification",
    }),
    async save_retention() {
      this.retention_saving = true;
      try {
        await this.update_attendance_retention(this.retention_months);
        this.set_notification({
          message: "Retention setting saved. It applies on your next import.",
          color: "success",
          showing: true,
          icon: "mdi-check",
        });
      } catch (error) {
        this.set_notification({
          message: "Failed to save retention setting.",
          color: "error",
          showing: true,
          icon: "mdi-close",
        });
      }
      this.retention_saving = false;
    },
    show_alert_bar(message, type) {
      this.show_alert = true;
      this.alert_message = message;
      this.alert_type = type;
      setTimeout(() => {
        this.show_alert = false;
      }, 6000);
    },
    verify() {
      if (this.authentication == this.authentication_code) {
        this.get_devices().then(() => {
          this.authenticated = true;
          this.authentication_dialog = false;
          this.show_alert_bar("Authenticated", "success");
        });
      } else {
        this.show_alert_bar("Access Denied", "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.account-stat {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  height: 100%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
  }

  &__label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
  }

  &__value {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin-top: 2px;
    word-break: break-word;
  }
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.section-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}
</style>
